export const FONT_SIZE_COOKIE = "ms-font-size";

const FONT_SIZE_STORAGE_KEY = "ms-font-size";

export const FONT_SIZES = ["small", "default", "large"] as const;

export type FontSize = (typeof FONT_SIZES)[number];

/**
 * Parses a stored font-size value.
 *
 * @param value - Cookie or storage value.
 * @returns A known font size, or `"default"` when missing or invalid.
 */
export function parseFontSize(value: string | undefined | null): FontSize {
	if (value === "small" || value === "large") return value;
	return "default";
}

/**
 * Returns the `--spacing` override for a font size, or `undefined` for the theme default.
 *
 * @param size - Selected font size.
 */
export function fontSizeSpacing(size: FontSize): string | undefined {
	if (size === "small") return "calc(0.25rem * 0.875)";
	if (size === "large") return "calc(0.25rem * 1.25)";
	return undefined;
}

/**
 * Returns an inline script that applies the persisted font size before first paint.
 */
export function getFontSizeInitScriptContent(): string {
	const sizes = JSON.stringify({ small: fontSizeSpacing("small"), large: fontSizeSpacing("large") });
	return `(function(){var m=document.cookie.match(/(?:^|; )${FONT_SIZE_COOKIE}=([^;]*)/);var s=m&&${sizes}[decodeURIComponent(m[1])];if(s)document.documentElement.style.setProperty("--spacing",s)})();`;
}

/**
 * Reads the font-size cookie in the browser.
 */
export function readClientFontSize(): FontSize {
	const match = document.cookie.match(new RegExp(`(?:^|; )${FONT_SIZE_COOKIE}=([^;]*)`));
	return parseFontSize(match?.[1] ? decodeURIComponent(match[1]) : undefined);
}

/**
 * Applies a font size by setting `--spacing` and persisting the choice in a cookie.
 *
 * @param size - Selected font size.
 */
export function applyFontSize(size: FontSize): void {
	const spacing = fontSizeSpacing(size);

	if (spacing) {
		document.documentElement.style.setProperty("--spacing", spacing);
	} else {
		document.documentElement.style.removeProperty("--spacing");
	}

	document.documentElement.removeAttribute("data-font-size");

	const secure = window.location.protocol === "https:" ? "; Secure" : "";
	document.cookie = `${FONT_SIZE_COOKIE}=${size}; Path=/; Max-Age=31536000; SameSite=Lax${secure}`;
}

/**
 * Migrates a legacy localStorage value into the cookie once.
 *
 * @returns The stored font size after migration.
 */
export function readAndMigrateClientFontSize(): FontSize {
	const fromCookie = readClientFontSize();
	if (document.cookie.split("; ").some((part) => part.startsWith(`${FONT_SIZE_COOKIE}=`))) {
		return fromCookie;
	}

	try {
		const fromStorage = parseFontSize(window.localStorage.getItem(FONT_SIZE_STORAGE_KEY));
		if (fromStorage !== "default") {
			applyFontSize(fromStorage);
			return fromStorage;
		}
	} catch {
		// Ignore storage failures (private mode, quota, etc).
	}

	return "default";
}
